import { useQuery } from '@tanstack/react-query';
import { countries } from 'country-data-list';
import { Users } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { MEDIA_URL } from '@/config';

import { fetchUserTeams } from '../api/getUserTeamsBySteamid';

interface UserTeamsProps {
  userId: string;
}
const UserTeams: React.FC<UserTeamsProps> = ({ userId }) => {
  const { data: userTeams, isLoading } = useQuery({
    queryKey: ['userTeams', userId],
    queryFn: () => fetchUserTeams(userId),
  });

  return (
    <div>
      <div className="mb-3 text-lg font-semibold">Teams</div>
      <div className="grid grid-cols-2 gap-x-8 gap-y-4 rounded-xl border border-border px-4 py-3 lg:grid-cols-3">
        {isLoading || !userTeams ? (
          <>
            {Array.from({ length: 3 }, (_, index) => (
              <div
                className="flex flex-col items-center gap-1.5 rounded-md px-3 py-4 sm:flex-row sm:gap-3"
                key={index}
              >
                <Skeleton className="h-[88px] w-[88px] rounded-md" />
                <div className="flex flex-col p-1">
                  <Skeleton className="mb-3 h-2.5 w-28" />
                  <Skeleton className="h-2.5 w-16" />
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            {userTeams.map((team) => (
              <Link
                className="flex cursor-pointer flex-col items-center gap-1.5 rounded-md px-3 py-4 transition-colors hover:bg-muted/50 sm:flex-row sm:gap-3"
                key={team.team_name}
                to={`/team/${team.id}`}
                title={`Go to ${team.team_name}`}
              >
                <Avatar className="h-[88px] w-[88px] !rounded-md">
                  <AvatarImage
                    src={`${MEDIA_URL}/images/uploads/${team.team_photo}`}
                    alt={team.team_name || ''}
                    className="!rounded-md"
                  />
                  <AvatarFallback className="rounded-md bg-secondary">
                    <Users />
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col p-1">
                  <h2 className="text-semibold font-medium">
                    {team.team_name}
                  </h2>
                  <span className="mb-3 text-xs text-muted-foreground">
                    {
                      countries.all.find(
                        (country) => country.alpha2 == team.country,
                      )?.name
                    }
                  </span>
                </div>
              </Link>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export { UserTeams };
